import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Loader2, Download, Send, CheckCircle2, FileText, Trash2, Edit, ArrowRight, X, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { generateProposalPdf } from '../utils/proposalPdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_BADGE = {
  draft: { label: 'Borrador', cls: 'bg-gray-100 text-gray-700' },
  sent: { label: 'Enviada', cls: 'bg-blue-100 text-blue-700' },
  accepted: { label: 'Aceptada', cls: 'bg-green-100 text-green-700' },
  converted: { label: 'Contrato generado', cls: 'bg-purple-100 text-purple-700' },
};

const emptyForm = {
  client_company_name: '',
  client_representative_name: '',
  client_representative_title: 'Sr.',
  client_ruc: '',
  client_address: '',
  client_city: 'Quito',
  client_country: 'Ecuador',
  client_email: '',
  client_phone: '',
  client_activity: '',
  software_name: '',
  software_description: '',
  modules: [],
  objective: '',
  scope_items: [],
  benefits: [],
  total_weeks: 8,
  schedule_activities: [],
  payment_phases: [{ description: 'Anticipo', amount: 0, week: 1 }],
  annual_costs: [],
  total_price: 0,
  proposal_date: new Date().toISOString().slice(0, 10),
};

const ProposalsTab = ({ onConverted }) => {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/proposals`);
      const items = r.data || [];
      // Check existence of linked contracts in parallel for "converted" proposals
      const enriched = await Promise.all(items.map(async p => {
        if (p.status === 'converted' && p.converted_contract_id) {
          try {
            await axios.get(`${API}/contracts/${p.converted_contract_id}`);
            return { ...p, _contract_missing: false };
          } catch (err) {
            if (err.response?.status === 404) return { ...p, _contract_missing: true };
            return p;
          }
        }
        return p;
      }));
      setProposals(enriched);
    } catch (e) {
      toast.error('Error al cargar propuestas');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setStep(1);
    setShowWizard(true);
  };

  const openEdit = async (p) => {
    setEditingId(p.id);
    setForm({
      ...emptyForm,
      ...p,
      proposal_date: p.proposal_date || new Date().toISOString().slice(0, 10),
    });
    setStep(1);
    setShowWizard(true);
  };

  const updateField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const totalFromPhases = form.payment_phases.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);

  const validate = () => {
    if (!form.client_company_name || !form.client_representative_name || !form.client_email) {
      toast.error('Completa los datos del cliente'); return false;
    }
    if (!form.client_activity || form.client_activity.length < 2) {
      toast.error('Indica la actividad del cliente'); return false;
    }
    if (!form.software_name || !form.objective || form.objective.length < 10) {
      toast.error('Completa nombre del software y objetivo'); return false;
    }
    if (!form.total_weeks || form.total_weeks < 1) {
      toast.error('Indica las semanas de ejecucion'); return false;
    }
    if (!form.payment_phases || form.payment_phases.length === 0 || totalFromPhases <= 0) {
      toast.error('Define al menos una fase de pago'); return false;
    }
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const payload = {
        ...form,
        total_price: totalFromPhases,
        payment_phases: form.payment_phases.map(p => ({
          description: p.description,
          amount: parseFloat(p.amount) || 0,
          week: parseInt(p.week) || 1,
        })),
        scope_items: (form.scope_items || []).filter(s => s && s.trim()),
        benefits: (form.benefits || []).filter(s => s && s.trim()),
      };
      if (editingId) {
        await axios.put(`${API}/proposals/${editingId}`, payload);
        toast.success('Propuesta actualizada');
      } else {
        await axios.post(`${API}/proposals`, payload);
        toast.success('Propuesta creada');
      }
      setShowWizard(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally { setBusy(false); }
  };

  const downloadPdf = async (p) => {
    try {
      await generateProposalPdf(p);
    } catch (e) {
      toast.error('Error generando PDF: ' + (e.message || e));
    }
  };

  const markSent = async (p) => {
    try {
      await axios.post(`${API}/proposals/${p.id}/mark-sent`);
      toast.success('Propuesta marcada como enviada');
      load();
    } catch (e) { toast.error('Error'); }
  };

  const markAccepted = async (p) => {
    try {
      await axios.post(`${API}/proposals/${p.id}/mark-accepted`);
      toast.success('Propuesta marcada como aceptada');
      load();
    } catch (e) { toast.error('Error'); }
  };

  const convertToContract = async (p, regenerate = false) => {
    let actuallyRegenerate = regenerate;
    let contractMissing = false;

    // Si la propuesta dice 'converted', verificar si el contrato sigue existiendo
    if (p.status === 'converted' && p.converted_contract_id) {
      try {
        await axios.get(`${API}/contracts/${p.converted_contract_id}`);
      } catch (err) {
        if (err.response?.status === 404) {
          contractMissing = true;
        }
      }
    }

    let confirmMsg;
    if (contractMissing) {
      confirmMsg = `El contrato anterior (${p.converted_contract_number}) fue eliminado. ¿Crear un nuevo contrato a partir de la propuesta ${p.proposal_number}?`;
    } else if (regenerate) {
      confirmMsg = `Regenerar contrato a partir de la propuesta ${p.proposal_number}? Se creara un contrato nuevo (el anterior queda sin tocar).`;
      actuallyRegenerate = true;
    } else {
      confirmMsg = `Generar contrato a partir de la propuesta ${p.proposal_number}?`;
    }
    if (!window.confirm(confirmMsg)) return;

    try {
      const r = await axios.post(
        `${API}/proposals/${p.id}/convert-to-contract`,
        null,
        { params: actuallyRegenerate ? { regenerate: true } : {} }
      );
      const action = r.data.regenerated || contractMissing ? 'recreado' : 'creado';
      toast.success(`Contrato ${r.data.contract_number} ${action} en borrador`);
      load();
      onConverted && onConverted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al generar contrato');
    }
  };

  const remove = async (p) => {
    if (!window.confirm('Eliminar la propuesta ' + p.proposal_number + '?')) return;
    try {
      await axios.delete(`${API}/proposals/${p.id}`);
      toast.success('Eliminada');
      load();
    } catch (e) { toast.error('Error'); }
  };

  return (
    <div className="space-y-5" data-testid="proposals-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#1a2456]">Propuestas Comerciales</h2>
          <p className="text-sm text-gray-500">Pre-paso al contrato. Una vez aceptada por el cliente se convierte en contrato con un click.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="refresh-proposals">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Actualizar
          </Button>
          <Button onClick={openNew} className="bg-[#4C8C3B] hover:bg-[#3d7030]" data-testid="new-proposal-btn">
            <Plus className="w-4 h-4 mr-2" /> Nueva Propuesta
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="text-left p-3">Numero</th>
                <th className="text-left p-3">Cliente</th>
                <th className="text-left p-3">Software</th>
                <th className="text-right p-3">Valor</th>
                <th className="text-left p-3">Estado</th>
                <th className="text-left p-3">Fecha</th>
                <th className="text-center p-3">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan="7" className="p-8 text-center text-gray-400"><Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Cargando...</td></tr>
              )}
              {!loading && proposals.length === 0 && (
                <tr><td colSpan="7" className="p-8 text-center text-gray-400">No hay propuestas. Cree la primera.</td></tr>
              )}
              {proposals.map(p => {
                const st = STATUS_BADGE[p.status] || STATUS_BADGE.draft;
                return (
                  <tr key={p.id} className="border-t hover:bg-gray-50">
                    <td className="p-3 font-mono text-xs">{p.proposal_number}</td>
                    <td className="p-3">
                      <div className="font-medium">{p.client_company_name}</div>
                      <div className="text-xs text-gray-500">{p.client_representative_name}</div>
                    </td>
                    <td className="p-3 text-gray-700">{p.software_name}</td>
                    <td className="p-3 text-right font-mono">${Number(p.total_price || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${st.cls}`}>{st.label}</span>
                      {p.converted_contract_number && !p._contract_missing && (
                        <div className="text-[10px] text-purple-700 mt-1">→ {p.converted_contract_number}</div>
                      )}
                      {p._contract_missing && (
                        <div className="text-[10px] text-red-600 mt-1 font-medium">Contrato eliminado</div>
                      )}
                    </td>
                    <td className="p-3 text-xs text-gray-500">{p.proposal_date}</td>
                    <td className="p-3">
                      <div className="flex items-center justify-center gap-1 flex-wrap">
                        <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => downloadPdf(p)} data-testid={`download-${p.proposal_number}`}>
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                        {p.status !== 'converted' && (
                          <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => openEdit(p)}>
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {p.status === 'draft' && (
                          <Button size="sm" variant="outline" className="h-7 px-2 text-blue-600" onClick={() => markSent(p)} title="Marcar como enviada">
                            <Send className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {p.status === 'sent' && (
                          <Button size="sm" variant="outline" className="h-7 px-2 text-green-600" onClick={() => markAccepted(p)} title="Marcar como aceptada">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {p.status === 'accepted' && (
                          <Button size="sm" className="h-7 px-2 bg-purple-600 hover:bg-purple-700 text-white text-xs" onClick={() => convertToContract(p)} data-testid={`convert-${p.proposal_number}`}>
                            <ArrowRight className="w-3.5 h-3.5 mr-1" /> Contrato
                          </Button>
                        )}
                        {p.status === 'converted' && p._contract_missing && (
                          <Button size="sm" className="h-7 px-2 bg-purple-600 hover:bg-purple-700 text-white text-xs"
                            onClick={() => convertToContract(p)}
                            title="El contrato fue eliminado. Click para crear uno nuevo desde esta propuesta."
                            data-testid={`recreate-${p.proposal_number}`}>
                            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Recrear contrato
                          </Button>
                        )}
                        {p.status === 'converted' && !p._contract_missing && (
                          <Button size="sm" variant="outline" className="h-7 px-2 text-purple-700 border-purple-200 text-xs"
                            onClick={() => convertToContract(p, true)}
                            title="Crear un contrato adicional desde esta propuesta (el anterior queda)"
                            data-testid={`regenerate-${p.proposal_number}`}>
                            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Regenerar
                          </Button>
                        )}
                        {p.status === 'draft' && (
                          <Button size="sm" variant="outline" className="h-7 px-2 text-red-600 border-red-200" onClick={() => remove(p)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showWizard && (
        <ProposalWizard
          form={form}
          step={step}
          setStep={setStep}
          updateField={updateField}
          onClose={() => setShowWizard(false)}
          onSave={save}
          busy={busy}
          editing={!!editingId}
          totalFromPhases={totalFromPhases}
        />
      )}
    </div>
  );
};

const ProposalWizard = ({ form, step, setStep, updateField, onClose, onSave, busy, editing, totalFromPhases }) => {
  const totalSteps = 4;

  const StepHeader = () => (
    <div className="flex items-center gap-2 mb-5">
      {[1, 2, 3, 4].map(s => (
        <React.Fragment key={s}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
            step === s ? 'bg-[#2F3D8E] text-white' : step > s ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
          }`}>{s}</div>
          {s < 4 && <div className={`flex-1 h-0.5 ${step > s ? 'bg-green-500' : 'bg-gray-200'}`} />}
        </React.Fragment>
      ))}
    </div>
  );

  const setListItem = (key, idx, value) => {
    const arr = [...(form[key] || [])];
    arr[idx] = value;
    updateField(key, arr);
  };
  const removeListItem = (key, idx) => {
    const arr = [...(form[key] || [])];
    arr.splice(idx, 1);
    updateField(key, arr);
  };
  const addListItem = (key) => updateField(key, [...(form[key] || []), '']);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl my-8" data-testid="proposal-wizard">
        <div className="flex items-center justify-between p-5 border-b">
          <h3 className="font-bold text-[#1a2456]">{editing ? 'Editar' : 'Nueva'} Propuesta — Paso {step} de {totalSteps}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6">
          <StepHeader />

          {step === 1 && (
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-700">Datos del Cliente y Actividad</h4>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <Label>Empresa *</Label>
                  <Input value={form.client_company_name} onChange={e => updateField('client_company_name', e.target.value)} data-testid="prop-company" />
                </div>
                <div>
                  <Label>Tratamiento</Label>
                  <select value={form.client_representative_title} onChange={e => updateField('client_representative_title', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md text-sm">
                    {['Sr.', 'Sra.', 'Ing.', 'Dr.', 'Lic.', 'Mgs.'].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Representante *</Label>
                  <Input value={form.client_representative_name} onChange={e => updateField('client_representative_name', e.target.value)} />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input type="email" value={form.client_email} onChange={e => updateField('client_email', e.target.value)} />
                </div>
                <div>
                  <Label>Telefono *</Label>
                  <Input value={form.client_phone} onChange={e => updateField('client_phone', e.target.value)} />
                </div>
                <div>
                  <Label>RUC</Label>
                  <Input value={form.client_ruc} onChange={e => updateField('client_ruc', e.target.value)} />
                </div>
                <div>
                  <Label>Ciudad</Label>
                  <Input value={form.client_city} onChange={e => updateField('client_city', e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <Label>Direccion</Label>
                  <Input value={form.client_address} onChange={e => updateField('client_address', e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <Label>Actividad del Cliente *</Label>
                  <Textarea rows={2} value={form.client_activity} onChange={e => updateField('client_activity', e.target.value)}
                    placeholder="Ej: Comercializacion de productos farmaceuticos en Ecuador"
                    data-testid="prop-activity" />
                  <p className="text-[11px] text-gray-500 mt-1">Se usa en la introduccion: "...alineada a las necesidades de su operacion (esta actividad)..."</p>
                </div>
                <div>
                  <Label>Fecha</Label>
                  <Input type="date" value={form.proposal_date} onChange={e => updateField('proposal_date', e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-700">Solucion y Alcance</h4>
              <div>
                <Label>Nombre del Software *</Label>
                <Input value={form.software_name} onChange={e => updateField('software_name', e.target.value)} data-testid="prop-software" />
              </div>
              <div>
                <Label>Objetivo de la Solucion *</Label>
                <Textarea rows={3} value={form.objective} onChange={e => updateField('objective', e.target.value)} data-testid="prop-objective"
                  placeholder="Describa que problema resuelve y que va a entregar la solucion..." />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Alcance General del Desarrollo</Label>
                  <Button type="button" size="sm" variant="outline" onClick={() => addListItem('scope_items')}>
                    <Plus className="w-3 h-3 mr-1" /> Agregar
                  </Button>
                </div>
                {(form.scope_items || []).map((s, i) => (
                  <div key={i} className="flex items-center gap-2 mb-1.5">
                    <Input value={s} onChange={e => setListItem('scope_items', i, e.target.value)} placeholder={`Punto de alcance ${i + 1}`} />
                    <Button size="sm" variant="ghost" onClick={() => removeListItem('scope_items', i)} className="text-red-500 hover:bg-red-50">
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
                {(!form.scope_items || form.scope_items.length === 0) && (
                  <p className="text-xs text-gray-400 italic">Sin items aun. Agregue puntos como "Modulo de carga", "Dashboard interactivo", etc.</p>
                )}
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Beneficios Clave (operacion, garantia y acompañamiento)</Label>
                  <Button type="button" size="sm" variant="outline" onClick={() => addListItem('benefits')}>
                    <Plus className="w-3 h-3 mr-1" /> Agregar
                  </Button>
                </div>
                {(form.benefits || []).map((s, i) => (
                  <div key={i} className="flex items-center gap-2 mb-1.5">
                    <Input value={s} onChange={e => setListItem('benefits', i, e.target.value)} placeholder={`Beneficio ${i + 1}`} />
                    <Button size="sm" variant="ghost" onClick={() => removeListItem('benefits', i)} className="text-red-500 hover:bg-red-50">
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-700">Plan de Ejecucion (Cronograma)</h4>
              <div>
                <Label>Semanas totales *</Label>
                <Input type="number" min={1} max={52} className="w-32" value={form.total_weeks}
                  onChange={e => updateField('total_weeks', parseInt(e.target.value) || 1)} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Actividades del cronograma</Label>
                  <Button type="button" size="sm" variant="outline"
                    onClick={() => updateField('schedule_activities', [...(form.schedule_activities || []), { activity: '', weeks: [] }])}>
                    <Plus className="w-3 h-3 mr-1" /> Agregar actividad
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mb-2">Se mostrara en el PDF como tabla de actividades x semanas marcadas.</p>
                {(form.schedule_activities || []).map((a, i) => (
                  <div key={i} className="border rounded-lg p-2.5 mb-2 bg-gray-50">
                    <div className="flex items-center gap-2 mb-2">
                      <Input value={a.activity} placeholder="Nombre de la actividad"
                        onChange={e => {
                          const arr = [...form.schedule_activities];
                          arr[i] = { ...arr[i], activity: e.target.value };
                          updateField('schedule_activities', arr);
                        }} />
                      <Button size="sm" variant="ghost" onClick={() => removeListItem('schedule_activities', i)} className="text-red-500">
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {Array.from({ length: form.total_weeks || 8 }).map((_, w) => {
                        const week = w + 1;
                        const checked = (a.weeks || []).includes(week);
                        return (
                          <button key={w} type="button"
                            onClick={() => {
                              const arr = [...form.schedule_activities];
                              const cur = new Set(arr[i].weeks || []);
                              if (checked) cur.delete(week); else cur.add(week);
                              arr[i] = { ...arr[i], weeks: Array.from(cur).sort((a, b) => a - b) };
                              updateField('schedule_activities', arr);
                            }}
                            className={`w-7 h-7 rounded text-[10px] font-medium ${checked ? 'bg-[#2F3D8E] text-white' : 'bg-white border text-gray-500 hover:bg-blue-50'}`}>
                            S{week}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-700">Propuesta Financiera (Cláusula Sexta)</h4>
              <p className="text-xs text-gray-500">Defina las fases de pago. El total se calcula automaticamente.</p>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Fases de pago</Label>
                  <Button type="button" size="sm" variant="outline"
                    onClick={() => updateField('payment_phases', [...form.payment_phases, { description: '', amount: 0, week: 1 }])}>
                    <Plus className="w-3 h-3 mr-1" /> Agregar fase
                  </Button>
                </div>
                {(form.payment_phases || []).map((ph, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-end">
                    <div className="col-span-6">
                      <Input value={ph.description} placeholder="Descripcion (ej: Anticipo del 50%)"
                        onChange={e => {
                          const arr = [...form.payment_phases]; arr[i] = { ...arr[i], description: e.target.value };
                          updateField('payment_phases', arr);
                        }} />
                    </div>
                    <div className="col-span-3">
                      <Input type="number" step="0.01" placeholder="Monto" value={ph.amount}
                        onChange={e => {
                          const arr = [...form.payment_phases]; arr[i] = { ...arr[i], amount: parseFloat(e.target.value) || 0 };
                          updateField('payment_phases', arr);
                        }} />
                    </div>
                    <div className="col-span-2">
                      <Input type="number" min={1} placeholder="Sem." value={ph.week}
                        onChange={e => {
                          const arr = [...form.payment_phases]; arr[i] = { ...arr[i], week: parseInt(e.target.value) || 1 };
                          updateField('payment_phases', arr);
                        }} />
                    </div>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => removeListItem('payment_phases', i)}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
                <span className="text-sm text-blue-900">Total propuesta:</span>
                <span className="text-lg font-bold text-[#2F3D8E]">${totalFromPhases.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
          <div>
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(s => s - 1)}>Anterior</Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onClose}>Cancelar</Button>
            {step < totalSteps ? (
              <Button className="bg-[#2F3D8E] hover:bg-[#1f2d6e] text-white" onClick={() => setStep(s => s + 1)}>
                Siguiente
              </Button>
            ) : (
              <Button className="bg-[#4C8C3B] hover:bg-[#3d7030] text-white" onClick={onSave} disabled={busy} data-testid="save-proposal-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                {editing ? 'Guardar cambios' : 'Crear propuesta'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProposalsTab;
